import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Coach analytics — SMS invite funnel + WhatsApp blast performance.
 *
 * Two card rows:
 *   - Invite funnel: sent / delivered / consumed + delivery and
 *     consumption rates + median time-to-claim (humanized).
 *   - WhatsApp blasts: attempts / delivered / delivery rate +
 *     per-session list of the most recent blasts with sent/failed counts.
 *
 * Self-hides when the coach has no data yet (so it doesn't render an
 * empty zero-row block on day 1).
 */
export default function CoachAnalytics({ theme = 'dark' }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/coach/roster/analytics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return null;
  if (!data) return null;

  const smsTotal = data.sms?.totals?.sent_total || 0;
  const wabaAttempted = data.whatsapp?.totals?.attempted || 0;
  if (smsTotal === 0 && wabaAttempted === 0) {
    // Coach hasn't used either surface yet — skip the empty block.
    return null;
  }

  return (
    <section
      className="rounded-2xl p-5 space-y-5"
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.95)',
        border: '1px solid rgba(0,201,167,0.15)',
      }}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">
          Coach analytics
        </div>
        <h2 className="font-bold mt-0.5">How your outreach is performing</h2>
      </div>

      {smsTotal > 0 && (
        <SmsFunnel data={data.sms} />
      )}

      {wabaAttempted > 0 && (
        <WabaSummary data={data.whatsapp} />
      )}
    </section>
  );
}

function SmsFunnel({ data }) {
  const totals = data.totals || {};
  const counts = data.counts || {};
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-bold opacity-70">
          SMS invites
        </div>
        <div className="text-xs opacity-50">
          last {data.lookback_days || 90} days
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Sent" value={totals.sent_total} />
        <Stat
          label="Delivered"
          value={totals.delivered}
          sub={fmtPct(totals.delivery_rate_pct)}
        />
        <Stat
          label="Signed up"
          value={totals.consumed}
          sub={fmtPct(totals.consumption_rate_pct)}
          highlight
        />
      </div>

      <div className="text-xs opacity-70 space-y-1">
        <FunnelRow label="Pending" value={counts.pending} />
        <FunnelRow label="Sent (awaiting click)" value={counts.sent} />
        <FunnelRow label="Consumed (signed up)" value={counts.consumed} />
        <FunnelRow label="Expired" value={counts.expired} />
        <FunnelRow label="Revoked" value={counts.revoked} />
        {data.median_time_to_claim_seconds != null && (
          <div className="pt-2 mt-2 border-t" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
            <span className="opacity-70">Typical time to sign up after the SMS:</span>{' '}
            <strong>{fmtSeconds(data.median_time_to_claim_seconds)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function WabaSummary({ data }) {
  const totals = data.totals || {};
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-bold opacity-70">
          WhatsApp blasts
        </div>
        <div className="text-xs opacity-50">
          last {data.lookback_days || 30} days · {totals.blast_count || 0} blast{(totals.blast_count || 0) === 1 ? '' : 's'}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Attempts" value={totals.attempted} />
        <Stat label="Delivered" value={totals.delivered} highlight />
        <Stat label="Delivery rate" value={fmtPct(totals.delivery_rate_pct) || '—'} />
      </div>

      {(data.by_session || []).length > 0 && (
        <ul className="divide-y text-xs"
            style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
          {data.by_session.slice(0, 8).map((s, i) => (
            <li key={s.session_id || `manual-${i}`} className="py-2 flex items-center justify-between">
              <span className="opacity-70 truncate">
                {s.session_id ? `Card ${s.session_id.slice(-6)}` : 'Manual blast'}
                {s.last_blast_at && (
                  <> · {new Date(s.last_blast_at).toLocaleDateString()}</>
                )}
              </span>
              <span className="shrink-0">
                <span style={{ color: '#00C9A7' }}>{s.sent} sent</span>
                {s.failed > 0 && (
                  <span style={{ color: '#ef4444' }}> · {s.failed} failed</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div
      className="rounded-lg p-3"
      style={{
        background: highlight
          ? 'linear-gradient(135deg, rgba(0,166,81,0.12), rgba(0,217,255,0.10))'
          : 'rgba(255,255,255,0.04)',
        border: `1px solid ${highlight ? 'rgba(0,201,167,0.3)' : 'rgba(150,160,180,0.2)'}`,
      }}
    >
      <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">{label}</div>
      <div className="text-2xl font-extrabold mt-0.5">
        {value == null ? '—' : value}
      </div>
      {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function FunnelRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between">
      <span className="opacity-70">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function fmtPct(pct) {
  if (pct == null) return null;
  return `${pct}%`;
}

function fmtSeconds(s) {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}
